import { Inject, Injectable } from '@angular/core';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable ,  of,  throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { HttpErrorHandlerService, HandleError } from './http-error-handling.service';
import { environment, CustomEnvironment, setApiOverrides } from '../../environments/environment';
import { query } from '@angular/animations';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type':  'application/json',
    'Authorization': 'my-auth-token'
  })
};

@Injectable({
  providedIn: 'root'
})
export class LookupService {
  private handleError: HandleError;
  private cache = {};

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService,
              @Inject(CustomEnvironment) customEnvironment: any) {
    setApiOverrides(customEnvironment);

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      const tokenUpdatedEvents = ['token_refreshed', 'token_received', 'silently_refreshed'];
      if (tokenUpdatedEvents.includes(event.type)) {
        httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
      }
    });
    
    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('LookupService');
  }

  /**
   * This method allows an in-memory cache
   * of a list of observables based on a "query" string
   * @param method 
   * @param query 
   * @param request 
   * @returns 
   */
  private cachedRequest(method, query: any, request: Observable<any>) {
    if (!this.cache[method]) {
      this.cache[method] = {};
    }

    if (query && this.cache[method][query]) {
      return this.cache[method][query];
    }
    const subject = new BehaviorSubject<any>(null);
    this.cache[method][query] = subject.asObservable();
    request.toPromise().then((val) => {
      subject.next(val);
    });
    return this.cache[method][query];
  }

  public getHttpHeaderForTextRequest(): any {
    return {
      headers: new HttpHeaders({
        'Authorization': this.oauthService.authorizationHeader()
      })
    };
  }

  // get a list of results from an ABN/ACN query
  getBusinessDetails(query): Observable<any> {
    return this.http.get<any[]>(`${environment.api.client}/ext/orgid/search?on=&abn=${query}`, httpOptions)
      .pipe(
        catchError(this.handleError('getBusinessDetails', []))
      );
  }

  // get a list of results from an org query
  getOrgList(query): Observable<any> {
    return this.http.get<any[]>(`${environment.api.client}/ext/orgid/seek?org=${query}`, httpOptions)
      .pipe(
        catchError(this.handleError('getOrgList', []))
      );
  }

  carLookup(query): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.lcaDomain}/apply/car-loan/vehicle/search?page=1&pageSize=10&maxAgeYears=7&frag=${query}`, httpOptions)
      .pipe(
        catchError(this.handleError('carLookup', []))
      );
  }
  
  addressLookupInternal(query): Observable<any> {
    const method = 'addressLookupInternal';
    if (query === '' || query === null) {
      return of([]);
    }
    return this.cachedRequest(method, query, this.http.get<any[]>(`${environment.api.client}/address/search?frag=${query}`, httpOptions));
  }

  addressLookupExternal(query): Observable<any> {
    const method = 'addressLookupExternal';
    if (query === '' || query === null) {
      return of([]);
    }
    return this.cachedRequest(method, query, this.http.get<any[]>(`${environment.api.aux}/address/search?frag=${query}`, httpOptions));
  }

  getAddressByAid(aid): Observable<any> {
    const method = 'getAddressByRpId';
    return this.cachedRequest(method, aid, this.http.get<any[]>(`${environment.api.client}/address?aid=${aid}`, httpOptions));
  }

  getAddressByRpId(rpId): Observable<any>  {
    const method = 'getAddressByRpId';
    return this.cachedRequest(method, rpId, this.http.get<any[]>(`${environment.api.lcaDomain}/apply/car-loan/addr?aid=&rp=${rpId}`, httpOptions));
  }

  suburbLookup(query): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.lcaDomain}/apply/car-loan/addr/suburb?frag=${query}&page=1&pageSize=30`, httpOptions)
      .pipe(
        catchError(this.handleError('suburbLookup', []))
      );
  }

  searchInsurers(query): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.client}/party/search?pt=2&prt=56&prc=&prg=&prs=&name=${query}&g=false&oid=&mineOnly=false&p=&ps=-1`, httpOptions)
      .pipe(
        catchError(this.handleError('searchInsurers', []))
      );
  }

  searchSuppliers(query): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.client}/party/search?pt&prt&prc=&prg=&prs=16&name=${query}&g=false&oid=&mineOnly=false&p=&ps=-1`, httpOptions)
      .pipe(
        catchError(this.handleError('searchSuppliers', []))
      );
  }

  searchBsb(query): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.client}/bankaccount/bsb-search?q=${query}`, httpOptions)
      .pipe(
        catchError(this.handleError('searchBsb', []))
      );
  }

  searchBillerCode(query): Observable<any> {
    return this.http.get<any[]>(
      `${environment.api.apiIbLoans}/dashboard/bpaybillers/search?billerCode=${query}`, httpOptions)
      .pipe(
        catchError(this.handleError('searchBsb', []))
      );
  }

  public sensisVerifyPhone(phoneNumber): Promise<any> {
    return this.http
      .get(`${environment.api.lcaDomain}/API/TotalCheckApi/api/Validation/phone?number=${phoneNumber}`)
      .pipe(
        catchError(this.handleError('sensisVerifyPhone', {}))
      )
      .toPromise();
  }

  public sensisVerifyEmail(email): Promise<any> {
    return this.http
      .get(`${environment.api.lcaDomain}/API/TotalCheckApi/api/Validation/email?email_address=${email}`)
      .pipe(
        catchError(this.handleError('sensisVerifyEmail', {}))
      )
      .toPromise();
  }
  
  /**
   * Update lead status
   * @param phoneNumber the lead ID
   * @returns {Observable<A>}
   */
   salesVerifyPhone(phoneNumber) {
    return this.http.get<any[]>(`${environment.api.sales}/validators/phone-number?number=${phoneNumber}`, httpOptions);
  }

  /**
   * To be implemented
   * @param query
   */
  public valuationLookup(query): Promise<any> {
    //TODO fix this api when work is completed
    return Promise.resolve(null);
/*     return this.http.get<any[]>(`${environment.api.fmcHomeloan}/api/broker/api/Valuation/SearchForExternalValuationReport?existingValuationID=${query}`, this.getHttpHeaderForTextRequest())
      .pipe(
        catchError(this.handleError('valuationLookup', null))
      )
      .toPromise();
 */  }
}
